import { storeToRefs } from 'pinia'

import { useUserBandStore } from '~/stores/userBand'

import type { BadgeName } from '~/components/influencer/badgeIcon.vue'
import type { Influencer, StatsV3Influencer } from '~/types/influencer'

export function useCuratorDisplayedPills(
  influencer: Influencer | StatsV3Influencer,
) {
  const { $pinia } = useNuxtApp()
  const { t } = useI18n()

  const { GET_INTERACTION_FROM_INFLUENCER_ID } = storeToRefs(
    useInfluencersInteractionsStore($pinia),
  )

  const { SELECTED_TAGS } = storeToRefs(useDraftStore($pinia))
  const { BAND_DATA } = storeToRefs(useUserBandStore($pinia))

  const { daysLeft, curatorIsContest } = useCuratorContestsInfos(influencer)
  const { GET_TAG_FROM_ID } = useTagStore($pinia)

  const highestPriorityBadgeName = useGetHighestPriorityBadgeName(
    influencer.tags.identity.influencer_badge,
  )

  const hasBadge = computed<boolean>(
    () =>
      (influencer.tags.identity.influencer_badge?.length > 0 ||
        Number(influencer.cost) > 2) ??
      false,
  )

  const highlightedPill = computed(() => {
    const { shared: sharedTracksCount, track_sent: sentTracksCount } =
      GET_INTERACTION_FROM_INFLUENCER_ID.value(influencer.id) ?? {
        shared: 0,
        track_sent: 0,
      }

    if (
      curatorIsContest(influencer) &&
      daysLeft.value &&
      daysLeft.value >= 0 &&
      daysLeft.value <= 10
    ) {
      return {
        iconClass: 'fa6-solid:clock',
        iconText: t('influencer.card.info01', { daysLeft: daysLeft.value }),
      }
    } else if (sharedTracksCount) {
      return {
        iconClass: 'fa6-solid:hands-clapping',
        iconText: t('influencer.card.info.interactions.sharedTracks', {
          amount: sharedTracksCount,
        }),
      }
    } else if (sentTracksCount) {
      return {
        iconClass: 'fa6-solid:circle-chevron-right',
        iconText: t('influencer.card.info.interactions.sentTracks', {
          count: sentTracksCount,
        }),
      }
    } else if (hasBadge.value) {
      return getDisplayedBadge()
    }

    return {
      iconClass: '',
      iconText: '',
      iconDescription: '',
    }
  })

  function getDisplayedBadge(badgeName: BadgeName | '' = '') {
    const displayedBadge = badgeName || highestPriorityBadgeName.value

    const UI_ICON_NAME = {
      'top-media': 'fa6-solid:star',
      'spotify-playlist': 'fa6-brands:spotify',
      'youtube-channel': 'fa6-brands:youtube',
      impacting: 'fa6-solid:rocket',
      'quality-feedbacks': 'fa6-brands:microblog',
      'event-organizer': 'fa6-regular:calendar-check',
      'high-answer-rate': 'fa6-solid:trophy',
      'high-sharing-rate': 'fa6-regular:thumbs-up',
      'highly-selective': 'fa6-solid:eye',
    } as const

    return {
      name: displayedBadge,
      iconClass: UI_ICON_NAME[displayedBadge] ?? '',
      iconText: t(`tags.influencer_badge.${displayedBadge}`, {
        ...influencer,
      }),
      iconDescription: t(`tags.influencer_badge_tooltip.${displayedBadge}`, {
        topMediaCost: Number(influencer.cost),
      }),
    }
  }

  /** Highlighted Genres
   * We select the 5 most relevant genres to display based on context. Priority is given to:
   *  1 - the genres selected in the filters (draftStore.SELECTED_TAGS.subgenre)
   *  2 - the ones that match the artist's genres
   *  3 - the ones from influencer.latest_stats.genres_accepted_often
   *  4 - the remaining from influencer.tags.liked
   * ***/
  const highlightedSubgenres = computed(() => {
    const selectedFiltersGenres = SELECTED_TAGS.value.subgenre ?? []
    const artistGenres = BAND_DATA.value?.tags.identity.subgenre ?? []
    const influencerAcceptedOftenGenres =
      influencer.latest_stats?.genres_accepted_often ?? []
    const influencerGenres = influencer.tags.liked?.subgenre ?? []

    const comparisonKeys = [
      'isSelected',
      'isInArtistGenres',
      'isAcceptedOften',
    ] as const
    type ComparisonKey = (typeof comparisonKeys)[number]
    type ComparisonHash = {
      [key: number]: { [key in ComparisonKey]: number }
    }

    const computeRank = (arr: number[], genre: number) =>
      arr.length - (arr.includes(genre) ? arr.indexOf(genre) : arr.length)

    const mappedGenres: ComparisonHash = influencerGenres.reduce(
      (acc, genre) => ({
        ...acc,
        [genre]: {
          isSelected: computeRank(selectedFiltersGenres, genre),
          isInArtistGenres: computeRank(artistGenres, genre),
          isAcceptedOften: computeRank(influencerAcceptedOftenGenres, genre),
        },
      }),
      {},
    )

    return influencerGenres
      .toSorted(
        (a, b) =>
          mappedGenres[b].isSelected - mappedGenres[a].isSelected ||
          mappedGenres[b].isInArtistGenres - mappedGenres[a].isInArtistGenres ||
          mappedGenres[b].isAcceptedOften - mappedGenres[a].isAcceptedOften ||
          a - b,
      )
      .map((genreId) =>
        t(`tags.subgenre.${GET_TAG_FROM_ID(genreId)?.name ?? ''}`),
      )
      .slice(0, 5)
  })

  return {
    hasBadge,
    highestPriorityBadgeName,
    getDisplayedBadge,
    highlightedSubgenres,
    highlightedPill,
  }
}
